import { useState, useCallback } from 'react';
import { useAudience } from './useAudience';

export const useAudiencePreview = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  
  const { previewAudience, previewComplexFilter } = useAudience();
  
  const previewWithTimeout = useCallback(async (audienceData, timeoutMs = 8000) => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Handle complex filters
      if (audienceData?.complex_filter_slug) {
        console.log('Previewing complex filter:', audienceData.complex_filter_slug);
        
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
        
        try {
          const results = await previewComplexFilter(audienceData.complex_filter_slug);
          clearTimeout(timeoutId);
          
          setResults(results);
          return { success: true, data: results };
        } catch (previewError) {
          clearTimeout(timeoutId);
          console.error('Complex filter preview failed:', previewError);
          
          // Return a safe fallback response
          const fallbackResults = {
            matched_count: 'Unknown',
            sample_clients: [],
            error: 'Preview temporarily unavailable - this will not affect automation creation'
          };
          
          setResults(fallbackResults);
          setError('Preview failed - automation can still be created');
          return { success: false, error: 'Preview failed', data: fallbackResults };
        }
      }
      
      // Handle regular audience data
      if (!audienceData?.groups || !Array.isArray(audienceData.groups)) {
        const emptyResults = {
          matched_count: 0,
          sample_clients: [],
          error: 'No audience conditions defined'
        };
        
        setResults(emptyResults);
        return { success: true, data: emptyResults };
      }
      
      // Convert audience data to API format
      const apiData = {
        match_all: [],
        match_any: []
      };
      
      audienceData.groups.forEach(group => {
        const formattedConditions = (group.conditions || []).map(condition => ({
          field: `${condition.recordType}.${condition.fieldName}`,
          operator: condition.operator,
          value: condition.value
        }));

        if (group.type === 'ALL' && formattedConditions.length > 0) {
          apiData.match_all.push({
            conjunction: 'AND',
            conditions: formattedConditions
          });
        }

        if (group.type === 'ANY' && formattedConditions.length > 0) {
          apiData.match_any.push({
            conjunction: 'OR',
            conditions: formattedConditions
          });
        }
      });
      
      // Check if we have valid conditions
      const hasAllConditions = apiData.match_all.length > 0 && 
        apiData.match_all[0].conditions && apiData.match_all[0].conditions.length > 0;
      const hasAnyConditions = apiData.match_any.length > 0 && 
        apiData.match_any[0].conditions && apiData.match_any[0].conditions.length > 0;
      
      if (!hasAllConditions && !hasAnyConditions) {
        const emptyResults = {
          matched_count: 0,
          sample_clients: [],
          error: 'No valid audience conditions'
        };
        
        setResults(emptyResults);
        return { success: true, data: emptyResults };
      }
      
      // Make the preview request with timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
      
      try {
        const results = await previewAudience(apiData);
        clearTimeout(timeoutId);
        
        setResults(results);
        return { success: true, data: results };
      } catch (previewError) {
        clearTimeout(timeoutId);
        console.error('Audience preview failed:', previewError);
        
        // Return a safe fallback response
        const fallbackResults = {
          matched_count: 'Unknown',
          sample_clients: [],
          error: 'Preview temporarily unavailable - this will not affect automation creation'
        };
        
        setResults(fallbackResults);
        setError('Preview failed - automation can still be created');
        return { success: false, error: 'Preview failed', data: fallbackResults };
      }
      
    } catch (error) {
      console.error('Preview error:', error);
      
      const errorResults = {
        matched_count: 'Unknown',
        sample_clients: [],
        error: 'Preview unavailable - automation can still be created'
      };
      
      setResults(errorResults);
      setError('Preview unavailable');
      return { success: false, error: error.message, data: errorResults };
    } finally {
      setIsLoading(false);
    }
  }, [previewAudience, previewComplexFilter]);
  
  const clearResults = useCallback(() => {
    setResults(null);
    setError(null);
  }, []);
  
  return {
    isLoading,
    results,
    error,
    previewWithTimeout,
    clearResults
  };
}; 